import { UserInfo } from '../types';

interface AssessmentResults {
  userInfo: UserInfo;
  dimensionScores: { [key: string]: number };
  overallScore: number;
  maturityLevel: string;
  interestedInFollowUp: boolean;
}

export const generateReportHTML = (results: AssessmentResults): string => {
  const { userInfo, dimensionScores, overallScore, maturityLevel, interestedInFollowUp } = results;
  
  const dimensionsHTML = Object.entries(dimensionScores)
    .map(([dimension, score]) => `
      <tr>
        <td style="padding: 12px; border-bottom: 1px solid #E8F4FD;">${dimension}</td>
        <td style="padding: 12px; border-bottom: 1px solid #E8F4FD; text-align: center; font-weight: bold;">${score.toFixed(1)}/5.0</td>
      </tr>
    `)
    .join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>AI Maturity Assessment Report</title>
    </head>
    <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px;">
      <div style="background: linear-gradient(135deg, #0D7FE9 0%, #0A5DAB 100%); color: white; padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 2rem;">AI Maturity Assessment Report</h1>
        <p style="margin: 10px 0 0 0; opacity: 0.95;">Powered by Kovaion</p>
      </div>
      
      <div style="background: white; padding: 40px; border: 1px solid #E8F4FD; border-top: none;">
        <div style="background: #E8F4FD; padding: 20px; border-radius: 8px; margin-bottom: 30px;">
          <h2 style="margin: 0 0 15px 0; color: #0D7FE9;">Participant Information</h2>
          <p style="margin: 5px 0;"><strong>Name:</strong> ${userInfo.name}</p>
          <p style="margin: 5px 0;"><strong>Email:</strong> ${userInfo.email}</p>
          <p style="margin: 5px 0;"><strong>Company:</strong> ${userInfo.company}</p>
          <p style="margin: 5px 0;"><strong>Job Title:</strong> ${userInfo.jobTitle}</p>
        </div>

        <div style="text-align: center; margin: 40px 0;">
          <div style="display: inline-block; background: #0D7FE9; color: white; padding: 15px 40px; border-radius: 50px; font-size: 1.5rem; font-weight: bold; box-shadow: 0 4px 12px rgba(13, 127, 233, 0.3);">
            ${maturityLevel}
          </div>
          <p style="margin: 20px 0; font-size: 1.2rem;">Overall Score: <strong>${overallScore.toFixed(1)} / 5.0</strong></p>
        </div>

        <h2 style="color: #0D7FE9; margin-top: 40px;">Dimension Breakdown</h2>
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background: #E8F4FD;">
              <th style="padding: 12px; text-align: left; border-bottom: 2px solid #0D7FE9;">Dimension</th>
              <th style="padding: 12px; text-align: center; border-bottom: 2px solid #0D7FE9;">Score</th>
            </tr>
          </thead>
          <tbody>
            ${dimensionsHTML}
          </tbody>
        </table>

        <div style="background: #FFF9E6; border-left: 4px solid #FFC107; padding: 20px; margin: 30px 0; border-radius: 4px;">
          <h3 style="margin: 0 0 10px 0; color: #F57C00;">Key Insights</h3>
          <p style="margin: 0;">Your results highlight key areas for improvement in your AI strategy. Focus on dimensions with lower scores to enhance your organization's AI maturity.</p>
        </div>

        ${interestedInFollowUp ? `
          <div style="background: #E8F7E8; border-left: 4px solid #4CAF50; padding: 20px; margin: 30px 0; border-radius: 4px;">
            <h3 style="margin: 0 0 10px 0; color: #2E7D32;">Follow-up Interest</h3>
            <p style="margin: 0;">The participant has expressed interest in a follow-up call to discuss their AI maturity assessment results.</p>
          </div>
        ` : ''}

        <div style="text-align: center; margin-top: 50px; padding-top: 30px; border-top: 2px solid #E8F4FD;">
          <p style="color: #666; font-size: 0.9rem;">Thank you for completing the AI Maturity Assessment</p>
          <p style="color: #0D7FE9; font-weight: bold; margin: 10px 0;">Kovaion</p>
          <p style="color: #999; font-size: 0.85rem;">Empowering AI Transformation</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

export const sendAssessmentEmails = async (results: AssessmentResults): Promise<boolean> => {
  try {
    const reportHTML = generateReportHTML(results);
    
    // Call the Azure Function API endpoint
    const response = await fetch('https://ai-assessment-emails.azurewebsites.net/api/send-assessment-report', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userInfo: results.userInfo,
        dimensionScores: results.dimensionScores,
        overallScore: results.overallScore,
        maturityLevel: results.maturityLevel,
        interestedInFollowUp: results.interestedInFollowUp,
        reportHTML: reportHTML,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send emails');
    }

    return true;
  } catch (error) {
    console.error('Error sending assessment emails:', error);
    return false;
  }
};
